import { CODEC_VERSION, CORPUS_ID, TITLE } from "./constants";

export interface ParsedPoem {
  codecVersion: string;
  corpusId: string;
  verseLines: string[];
}

const TITLE_PATTERN = /^〔诗帖 (v[0-9]+)·([a-z0-9-]{1,16})〕$/u;
const TITLE_PREFIX = /^〔[诗詩]帖/u;
const TITLE_PARTIAL = ["〔诗帖", "〔詩帖"] as const;
const SENTENCE_BOUNDARY = /[\r\n，。？！；：、,.?!;:]+/u;

export type PoemScanState = "empty" | "title-partial" | "title-invalid" | "partial" | "ok";

export interface ScannedPoem {
  state: PoemScanState;
  sentences: number;
  rows: number;
}

function firstNonEmptyLine(value: string): string | undefined {
  return value
    .split(/\r?\n|\r/u)
    .map((line) => line.trim())
    .find(Boolean);
}

function isPartialTitle(value: string): boolean {
  const line = firstNonEmptyLine(value);
  if (!line) return false;
  return TITLE_PARTIAL.some((prefix) => prefix.startsWith(line) || (line.startsWith(prefix) && !line.includes("〕")));
}

function splitSentences(value: string): string[] {
  return value
    .split(SENTENCE_BOUNDARY)
    .map((sentence) => sentence.trim())
    .filter(Boolean);
}

function withoutFirstNonEmptyLine(value: string): string {
  const lines = value.split(/\r?\n|\r/u);
  const index = lines.findIndex((line) => line.trim().length > 0);
  if (index < 0) return value;
  return [...lines.slice(0, index), ...lines.slice(index + 1)].join("\n");
}

export function formatPoem(verseLines: readonly string[]): string {
  if (verseLines.length % 2 !== 0) throw new RangeError("A poem must contain complete couplets.");
  const rows: string[] = [];
  for (let index = 0; index < verseLines.length; index += 2) {
    rows.push(`${verseLines[index]}，${verseLines[index + 1]}。`);
  }
  return `${TITLE}\n${rows.join("\n")}`;
}

export function parsePoem(poem: string): ParsedPoem {
  const firstLine = firstNonEmptyLine(poem);
  if (!firstLine) throw new PoemFormatError("missing-verse", "诗帖里还没有诗句。");

  let codecVersion = CODEC_VERSION;
  let corpusId = CORPUS_ID;
  let verse = poem;
  if (TITLE_PREFIX.test(firstLine)) {
    const canonicalTitle = firstLine.replaceAll("詩", "诗");
    const match = TITLE_PATTERN.exec(canonicalTitle);
    if (!match) throw new PoemFormatError("invalid-title", "题签无法识别，请检查完整的〔诗帖…〕标题行。");
    codecVersion = match[1]!;
    corpusId = match[2]!;
    if (codecVersion !== CODEC_VERSION || corpusId !== CORPUS_ID) {
      throw new PoemFormatError("unsupported-version", `暂不支持 ${codecVersion}·${corpusId}。`);
    }
    verse = withoutFirstNonEmptyLine(poem);
  }

  const verseLines = splitSentences(verse);
  if (verseLines.length === 0) throw new PoemFormatError("missing-verse", "诗帖里还没有诗句。");
  return { codecVersion, corpusId, verseLines };
}

export function scanPoem(poem: string): ScannedPoem {
  const firstLine = firstNonEmptyLine(poem);
  if (!firstLine) return { state: "empty", sentences: 0, rows: 0 };
  if (isPartialTitle(poem)) return { state: "title-partial", sentences: 0, rows: 0 };

  let verse = poem;
  if (TITLE_PREFIX.test(firstLine)) {
    const match = TITLE_PATTERN.exec(firstLine.replaceAll("詩", "诗"));
    if (!match || match[1] !== CODEC_VERSION || match[2] !== CORPUS_ID) {
      return { state: "title-invalid", sentences: 0, rows: 0 };
    }
    verse = withoutFirstNonEmptyLine(poem);
  }

  const sentences = splitSentences(verse).length;
  if (sentences === 0) return { state: "empty", sentences: 0, rows: 0 };
  return {
    state: sentences % 2 === 0 ? "ok" : "partial",
    sentences,
    rows: Math.ceil(sentences / 2),
  };
}

export class PoemFormatError extends Error {
  constructor(
    readonly code: "missing-verse" | "invalid-title" | "unsupported-version",
    message: string,
  ) {
    super(message);
    this.name = "PoemFormatError";
  }
}
